import { setType } from '@/common/utils/typedValueSerializer'
import type { EditableRegistryElement } from '@/common/utils/editableTableOnChangeRetry'

function readRowCellValues(row: unknown): unknown[] {
  if (!row || typeof row !== 'object') return []
  const r = row as Record<string, unknown>
  const fields = r.fields
  if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
    return Object.values(fields as Record<string, { value?: unknown }>).map((cell) =>
      cell && typeof cell === 'object' && 'value' in cell ? cell.value : cell,
    )
  }
  return Object.entries(r)
    .filter(([key]) => key !== 'fields' && !key.startsWith('_'))
    .map(([, value]) => value)
}

function isEmptyCellValue(value: unknown): boolean {
  return value === '' || value === null || value === undefined
}

/** Число непустых ячеек в UniDataTable (rows[].fields). */
export function countEditableTableFilledCells(tableEntry: unknown): number {
  if (!tableEntry || typeof tableEntry !== 'object') return 0
  const entry = tableEntry as { type?: string; value?: { rows?: unknown[] }; rows?: unknown[] }
  const rows =
    entry.value && typeof entry.value === 'object' && Array.isArray(entry.value.rows)
      ? entry.value.rows
      : Array.isArray(entry.rows)
        ? entry.rows
        : []
  let filled = 0
  for (const row of rows) {
    for (const cell of readRowCellValues(row)) {
      if (!isEmptyCellValue(cell)) filled += 1
    }
  }
  return filled
}

/**
 * Live EDITABLE в forms/registry «богаче» page snapshot (есть ячейки, которых нет на page).
 * Не даём syncPageDataToRegistry затирать локальные правки пустым/устаревшим page.
 */
export function editableTableRegistryRicherThanPage(
  registryEntry: unknown,
  pageEntry: unknown,
): boolean {
  return countEditableTableFilledCells(registryEntry) > countEditableTableFilledCells(pageEntry)
}

function isAuthoritativeEmptyEditableTable(entry: unknown): boolean {
  const typed = readEditableTableTypedEntry(entry)
  if (!typed) return false
  const value = typed.value as { rows?: unknown[]; empty?: boolean }
  if (value?.empty === true) return true
  return Array.isArray(value?.rows) && value.rows.length === 0 && value.empty !== false
}

function readEditableTableTypedEntry(raw: unknown): { type: string; value: unknown } | null {
  if (raw == null) return null
  if (typeof raw !== 'object') return null
  const entry = raw as { type?: string; value?: unknown; rows?: unknown[] }
  if (String(entry.type ?? '') === 'UniDataTable' && entry.value != null) {
    return { type: 'UniDataTable', value: entry.value }
  }
  if (Array.isArray(entry.rows)) {
    return { type: 'UniDataTable', value: { rows: entry.rows } }
  }
  if (entry.value && typeof entry.value === 'object' && Array.isArray((entry.value as { rows?: unknown[] }).rows)) {
    return { type: 'UniDataTable', value: entry.value }
  }
  return null
}

/**
 * Live UniDataTable из registry EDITABLE (forms getValue / registeredValue / getForm field).
 * Для overlay в createClientDataUniData — авторитетнее stale page snapshot.
 */
export function collectLiveEditableTableOverrides(
  formId: string | number | undefined,
  elements: Record<string, EditableRegistryElement | null | undefined>,
  options?: {
    pageDataSlot?: string
    getFormField?: (fieldName: string) => unknown
  },
): Record<string, { type: string; value: unknown }> {
  const wanted = formId != null && formId !== '' && formId !== -1 ? String(formId) : ''
  const wantedSlot = String(options?.pageDataSlot ?? '').trim()
  if (!wanted && !wantedSlot) return {}

  const out: Record<string, { type: string; value: unknown }> = {}

  for (const reg of Object.values(elements)) {
    if (!reg || String(reg.elementType ?? '').toUpperCase() !== 'EDITABLE') continue
    const formIdMatch = wanted !== '' && String(reg.formId ?? '') === wanted
    const slotMatch =
      wantedSlot !== '' && String(reg.pageDataSlot ?? '').trim() === wantedSlot
    if (!formIdMatch && !slotMatch) continue

    const fieldName = String(reg.fieldName ?? '').trim()
    if (!fieldName) continue

    const fromForm = options?.getFormField?.(fieldName)
    const fromRegistry =
      (typeof reg.getLiveFieldValue === 'function' ? reg.getLiveFieldValue() : undefined) ??
      reg.registeredValue ??
      reg.getValue?.()
    if (isAuthoritativeEmptyEditableTable(fromRegistry)) {
      out[fieldName] = { type: 'UniDataTable', value: { rows: [], empty: true } }
      continue
    }
    const candidates = [fromForm, fromRegistry]
    let best: { type: string; value: unknown } | null = null
    let bestFilled = -1
    for (const candidate of candidates) {
      const typed = readEditableTableTypedEntry(candidate)
      if (!typed) continue
      const filled = countEditableTableFilledCells(typed)
      if (filled > bestFilled) {
        best = typed
        bestFilled = filled
      }
    }
    if (best && bestFilled > 0) {
      out[fieldName] = best.type === 'UniDataTable' ? best : setType(best.value)
    }
  }

  return out
}
